// ServerErrorWatcher — polls /api/admin/logs?lvl=error when debug mode is
// enabled (localStorage key "bp_debug_mode") and surfaces new server errors
// as a persistent dialog with full message text.
//
// Usage: render once at the app level. It's a no-op when debug mode is off.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { DASHBOARD_PATHS } from "@/lib/paths";

const STORAGE_KEY = 'bp_debug_mode';
const LAST_SEEN_KEY = 'bp_debug_last_error_ts';
const POLL_MS = 30_000;

interface LogEntry { t: string; lvl: string; msg: string; }

export function ServerErrorWatcher() {
  const [debugMode, setDebugMode] = useState(() => localStorage.getItem(STORAGE_KEY) === '1');
  const [pending, setPending] = useState<LogEntry[]>([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Listen for other tabs toggling debug mode.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setDebugMode(e.newValue === '1');
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const poll = useCallback(async () => {
    const since = localStorage.getItem(LAST_SEEN_KEY) ?? '';
    try {
      const params = new URLSearchParams({ lvl: 'error', lines: '50' });
      if (since) params.set('since', since);
      const data = await api<{ entries?: LogEntry[] }>(`${DASHBOARD_PATHS.adminLogs}?${params}`);
      const entries: LogEntry[] = data.entries ?? [];
      if (entries.length > 0) {
        setPending((prev) => {
          const ids = new Set(prev.map((e) => e.t + e.msg.slice(0, 40)));
          const novel = entries.filter((e) => !ids.has(e.t + e.msg.slice(0, 40)));
          return novel.length > 0 ? [...novel, ...prev].slice(0, 100) : prev;
        });
        setOpen(true);
      }
    } catch { /* best-effort */ }
  }, []);

  useEffect(() => {
    if (!debugMode) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    // Initial poll deferred via queueMicrotask so the async setPending
    // inside `poll` lands in a callback rather than the effect body. The
    // setInterval branch already runs `poll` from a callback.
    queueMicrotask(poll);
    timerRef.current = setInterval(poll, POLL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [debugMode, poll]);

  const dismiss = () => {
    if (pending.length > 0) {
      localStorage.setItem(LAST_SEEN_KEY, pending[0].t);
    }
    setPending([]);
    setOpen(false);
  };

  if (!debugMode || !open || pending.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-red-400 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
            {pending.length} Server Error{pending.length > 1 ? 's' : ''}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {pending.map((entry, i) => (
            <div key={i} className="rounded-md border border-red-900/40 bg-red-950/20 p-3 text-xs font-mono">
              <div className="text-red-400 text-[10px] mb-1.5 font-sans font-medium">
                {new Date(entry.t).toLocaleTimeString()} — {entry.lvl.toUpperCase()}
              </div>
              <pre className="whitespace-pre-wrap break-words text-red-200/90 leading-relaxed">
                {entry.msg}
              </pre>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { navigate('/settings?tab=server-logs'); dismiss(); }}
          >
            View Server Logs
          </Button>
          <Button variant="destructive" size="sm" onClick={dismiss}>
            Dismiss All
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// `setDebugMode` + `getDebugModeEnabled` live in `./debugMode` so this file
// stays a components-only export surface (`react-refresh/only-export-components`).
